/**
 * Hono RPC Client
 *
 * Type-safe API client using Hono RPC.
 * Provides automatic type inference from the API's AppType.
 *
 * @module lib/rpc/client
 *
 * @example
 * ```tsx
 * // In a React component
 * const rpc = useRpc();
 * const res = await rpc.api.private.tables.$get();
 * const data = await res.json();
 *
 * // Or with query parameters
 * const res = await rpc.api.private.tables.$get({
 *   query: { limit: 10, offset: 0 }
 * });
 * ```
 */

import { hc } from 'hono/client';
import type { AppType } from '@loader/api-types';
import { ERROR_CODES } from '@loader/shared-types';

import { API_BASE_URL } from '@/config/constants';
import { useWorkspaceStore } from '@/store/useWorkspaceStore';
import { queryClient } from '@/lib/queryClient';


// Flag to prevent multiple 403 redirects
let isHandling403 = false;

/**
 * Retry configuration for transient errors
 */
const RETRY_CONFIG = {
  /** Maximum number of retry attempts */
  maxRetries: 3,
  /** Base delay in ms between retries (with exponential backoff) */
  baseDelayMs: 1000,
  /** Error messages that should trigger a retry */
  retryableMessages: [
    'User not found',
    'account initialization',
    'Please wait',
  ],
};


/**
 * URL patterns where 403 doesn't mean "lost workspace access"
 * These endpoints may return 403 for other reasons (e.g., not owner)
 */
const SKIP_403_REDIRECT_PATTERNS = [
  '/workspaces/all',     // Listing workspaces
  '/workspaces/select',  // Selecting workspace
  '/auth/',              // Auth endpoints
  '/webhooks/',          // Webhook endpoints
  '/membership',         // Membership checks (might 403 for other reasons)
] as const;

/**
 * Check if an error response should be retried
 */
async function shouldRetry(response: Response): Promise<boolean> {
  // Only retry 401 errors that might be initialization-related
  if (response.status !== 401) {
    return false;
  }

  try {
    const clonedResponse = response.clone();
    const text = await clonedResponse.text();
    return RETRY_CONFIG.retryableMessages.some(msg =>
      text.toLowerCase().includes(msg.toLowerCase())
    );
  } catch {
    return false;
  }
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Handle 403 Forbidden responses globally
 *
 * When user loses access to a workspace (NO_WORKSPACE, WORKSPACE_ACCESS_LOST,
 * or generic 403), clears local state and hard-redirects to /no-access where
 * the user can see what happened and choose their next action.
 */
async function handle403Response(url: string, response: Response): Promise<void> {
  // Skip handling for endpoints where 403 doesn't mean "lost workspace access"
  if (SKIP_403_REDIRECT_PATTERNS.some(pattern => url.includes(pattern))) {
    return;
  }

  // Parse the error code from the response body
  let code: string | undefined;
  try {
    const clonedResponse = response.clone();
    const data = await clonedResponse.json() as { code?: string } | null;
    code = data?.code;
  } catch {
    // Couldn't parse response
  }

  // NO_WORKSPACE = zero workspaces remaining. Always handle immediately
  // regardless of debounce — this does a hard redirect to /no-access.
  if (code === ERROR_CODES.NO_WORKSPACE) {
    isHandling403 = true;

    useWorkspaceStore.getState().clearSelectedWorkspace();
    queryClient.clear();

    window.location.replace('/no-access');
    return;
  }

  // WORKSPACE_ACCESS_LOST = lost access to selected workspace (may have others).
  // Always hard-redirect to /no-access so the user understands what happened
  // and can choose their next action (switch workspace, create one, etc.).
  if (code === ERROR_CODES.WORKSPACE_ACCESS_LOST) {
    isHandling403 = true;

    useWorkspaceStore.getState().clearSelectedWorkspace();
    queryClient.clear();

    window.location.replace('/no-access');
    return;
  }

  // Debounce generic 403 handling to prevent cascading handlers
  if (isHandling403) {
    return;
  }

  // Generic 403 — clear workspace and redirect
  if (window.location.pathname !== '/no-access') {
    isHandling403 = true;

    useWorkspaceStore.getState().clearSelectedWorkspace();
    queryClient.clear();

    window.location.replace('/no-access');
  }
}

/**
 * Custom fetch wrapper that adds authentication headers, handles 403, and retries on transient errors
 *
 * Retry behavior:
 * - Retries up to 3 times for "User not found" / initialization errors
 * - Uses exponential backoff (1s, 2s, 4s)
 * - This handles the case where user signs up and the webhook hasn't completed yet
 *
 * @param getToken - Function to get the auth token
 * @returns Fetch function with auth headers
 */
function createAuthFetch(getToken: () => Promise<string | null>) {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = await getToken();

    const headers = new Headers(init?.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Only set Content-Type for non-FormData requests
    // FormData needs the browser to set multipart/form-data with boundary
    const isFormData = init?.body instanceof FormData;
    if (!isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    // Perform request with retry logic for initialization errors
    let lastResponse: Response | null = null;
    for (let attempt = 0; attempt <= RETRY_CONFIG.maxRetries; attempt++) {
      const response = await fetch(input, {
        ...init,
        headers,
        credentials: 'include',
      });

      lastResponse = response;

      // Handle 403 Forbidden - user lost access to workspace
      if (response.status === 403) {
        const url = typeof input === 'string' ? input : input.toString();
        await handle403Response(url, response);

        // If the 403 handler cleared the workspace (access revoked), don't
        // return the 403 response — the calling queryFn would throw an error
        // that propagates to the error boundary before React can unmount the
        // workspace-scoped components. Returning a never-settling promise
        // keeps the query in "fetching" state while the _withWorkspace layout
        // re-renders and unmounts AppShellContent, at which point the query
        // observer is cleaned up.
        if (!useWorkspaceStore.getState().selectedWorkspaceId) {
          return new Promise<Response>(() => {});
        }

        return response;
      }

      // Check if we should retry (transient initialization errors)
      if (attempt < RETRY_CONFIG.maxRetries && await shouldRetry(response)) {
        const delay = RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt);
        await sleep(delay);
        continue;
      }

      return response;
    }

    // Should never reach here due to loop structure, but TypeScript needs this.
    if (!lastResponse) {
      throw new Error('Unexpected: no response received after retries');
    }
    return lastResponse;
  };
}

/**
 * Create a type-safe Hono RPC client
 *
 * @param getToken - Function to get the authentication token
 * @returns Typed Hono client with all API routes
 */
export function createRpcClient(getToken: () => Promise<string | null>) {
  return hc<AppType>(API_BASE_URL, {
    fetch: createAuthFetch(getToken),
  });
}

/**
 * Type for the Hono RPC client
 */
export type RpcClient = ReturnType<typeof createRpcClient>;
